/* LEGAL-NOTICE
   © Copyright 2018 - Quid Informatica S.p.A. - All Rights Reserved
   https://quidinfo.it/qinetic/licence.html - "Quid Consumer Lending Platform"
   Deposito SIAE n. 012702 del 26 Luglio 2018
LEGAL-NOTICE */
import { ApiService } from '@api/services/api/api.service';
import { forkJoin, Observable, of } from 'rxjs';
import { Injectable } from '@angular/core';
import { DomainItemDTO } from '@api/models/';
import { DomainMapModel, DomainOptionsModel } from '@models/.';
import { map, tap, delay } from 'rxjs/operators';
import { HttpParams } from '@angular/common/http';
import { catchError } from 'rxjs/operators';

@Injectable({
  providedIn: 'root'
})
export class DomainService {
  private cache: { [domainCode: string]: DomainItemDTO[] } = {};

  constructor(private apiService: ApiService) {}

  /**
   * carica il dominio richiesto
   */
  getDomain(
    domainCode: string,
    options?: DomainOptionsModel
  ): Observable<DomainItemDTO[]> {
    // determino se è consentito utilizzare la cache
    const cacheAllowed =
      !options || (options.fromCache !== false && !options.params);

    const cacheCode = this.getCacheCode(domainCode, options);

    if (cacheAllowed && this.doesCacheExist(cacheCode)) {
      return this.getFromCache(cacheCode, options && options.cacheDelay);
    }

    const params = this.getParamsFromOptions(options);

    return this.apiService
      .getDomain({ pathParams: { code: domainCode }, options: { params } })
      .pipe(
        map(res => this.removeDuplicates(res.domainList)),
        tap(list => {
          if (cacheAllowed) {
            this.saveCache(cacheCode, list);
          }
        })
      );
  }

  /**
   * carica lista di domini
   */
  getMultipleDomains(
    domainCodeList: string[],
    optionsList?: DomainOptionsModel[]
  ): Observable<{ code: string; domain: DomainItemDTO[] }[]> {
    return forkJoin(
      domainCodeList.map((code, index) =>
        this.getDomain(code, (optionsList && optionsList[index]) || null).pipe(
          catchError(() => of([]))
        )
      )
    ).pipe(
      map(res =>
        res.map((domain, index) => ({ code: domainCodeList[index], domain }))
      )
    );
  }

  /**
   * carica il dominio richiesto e lo restituisce sotto forma di mappa
   */
  getDomainMap(
    domainCode: string,
    options?: DomainOptionsModel
  ): Observable<DomainMapModel> {
    return this.getDomain(domainCode, options).pipe(
      map(res => this.listToMap(res))
    );
  }

  /**
   * carica lista di domini
   */
  getMultipleDomainMaps(
    domainCodeList: string[],
    optionsList?: DomainOptionsModel[]
  ): Observable<{ code: string; domain: DomainMapModel }[]> {
    return forkJoin(
      domainCodeList.map((code, index) =>
        this.getDomainMap(code, (optionsList && optionsList[index]) || null)
      )
    ).pipe(
      map(res =>
        res.map((domain, index) => ({ code: domainCodeList[index], domain }))
      )
    );
  }

  /**
   * pulisce la cache;
   * se viene specificato il domain code vengono puliti
   * tutte le cache che contengono quel dominio
   */
  cleanCache(domainCode?: string): void {
    if (!domainCode) {
      this.cache = {};
      return;
    }

    Object.keys(this.cache).forEach(cacheCode => {
      if (cacheCode.indexOf(domainCode) === 0) {
        delete this.cache[cacheCode];
      }
    });
  }

  /**
   * definisce il cache code di un dominio
   */
  private getCacheCode(
    domainCode: string,
    options?: DomainOptionsModel
  ): string {
    if (!!options && !!options.filter) {
      return `${domainCode}.${options.filter}`;
    } else {
      return domainCode;
    }
  }

  /**
   * determina se la cache esiste ed è popolata
   */
  private doesCacheExist(cacheCode: string): boolean {
    return !!this.cache[cacheCode] && this.cache[cacheCode].length > 0;
  }

  /**
   * restituisce il dominio dalla cache corrispondente al cache code
   */
  private getFromCache(
    cacheCode: string,
    waitAtLeast?: number
  ): Observable<DomainItemDTO[]> {
    if (typeof waitAtLeast !== 'number') {
      waitAtLeast = 50;
    }

    const list = this.cache[cacheCode] || [];

    return of(list).pipe(delay(waitAtLeast));
  }

  /**
   * salva il dominio nella cache
   */
  private saveCache(cacheCode: string, domain: DomainItemDTO[]): void {
    this.cache[cacheCode] = domain;
  }

  /**
   * crea gli http parama per la chiamata al dominio
   */
  private getParamsFromOptions(options: DomainOptionsModel): HttpParams {
    let params: HttpParams = new HttpParams();

    if (!options) {
      return params;
    }

    if (!!options.filter) {
      params = params.set('filter', options.filter);
    }

    if (!!options.params) {
      Object.keys(options.params).forEach(key => {
        params = params.set(key, options.params[key]);
      });
    }

    return params;
  }

  /**
   * converte la lista in mappa
   */
  private listToMap(domainList: DomainItemDTO[]): DomainMapModel {
    const domainMap: DomainMapModel = {};

    if (domainList instanceof Array) {
      domainList.forEach(item => (domainMap[item.key] = item));
    }

    return domainMap;
  }

  /**
   * rimuove gli item duplicati dalla lista
   */
  private removeDuplicates(domainList: DomainItemDTO[]): DomainItemDTO[] {
    return domainList.filter(
      (item, index, arr) => arr.findIndex(x => x.key === item.key) === index
    );
  }
}
